import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { OAuthService, TokenEvents, TokenHandlerService } from '@celerofinancas/ui-oauth';
import { filter } from 'rxjs';

/**
 * Demo login screen. Authentication is fully delegated to `ui-oauth` — the button
 * fires the OIDC flow through a popup (`initLoginFlow(true)`), with no
 * email/password form, exactly like `login.component.ts` in
 * `serasa-cl-webcomponents`. The point of this slice is for the whitelabel to work
 * on top of the same package used in production, unmodified.
 *
 * The popup only completes because `public/silent-refresh.html` posts the `code`
 * back to `window.opener` — see that file for the reasoning.
 *
 * Unlike Serasa, this screen navigates when the token arrives: there each screen is
 * a separate custom element the host swaps; here it is a single SPA with the Angular
 * Router, so without that redirect clicking the button completes the login and leads
 * nowhere. The destination is the `returnUrl` `authGuard` appended (the route the
 * user tried to open without a session), falling back to `/dashboard` when there is
 * none.
 */
@Component({
  selector: 'login',
  standalone: true,
  imports: [],
  templateUrl: './login.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoginComponent implements OnInit {
  /**
   * OAuth service, which loads the discovery document and starts the login flow.
   */
  private readonly oauthService = inject(OAuthService);

  /**
   * Token handler service, the source of the session events this screen listens to.
   */
  private readonly tokenHandler = inject(TokenHandlerService);

  /**
   * Router, used to navigate once the token arrives.
   */
  private readonly router = inject(Router);

  /**
   * Active route, read for the `returnUrl` the guard appends.
   */
  private readonly route = inject(ActivatedRoute);

  /**
   * Destroy reference, which unsubscribes the session events with the component.
   */
  private readonly destroyRef = inject(DestroyRef);

  /**
   * Loads the issuer's discovery document (the mock) and starts listening for the
   * token so it can navigate to the dashboard.
   */
  public ngOnInit(): void {
    this.oauthService.loadConfiguration();

    this.tokenHandler.eventsSubject$
      .pipe(
        filter((event) => event?.['event'] === TokenEvents.TOKEN_RECEIVED),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        const returnUrl = this.route.snapshot.queryParamMap.get('returnUrl');
        void this.router.navigateByUrl(returnUrl ?? '/dashboard');
      });
  }

  /**
   * Fires the OIDC login flow in a popup when the button is clicked.
   */
  public buttonClick(): void {
    this.oauthService.initLoginFlow(true);
  }
}
